import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { 
  Database, 
  Users, 
  BarChart, 
  Workflow, 
  ChevronRight,
  ShoppingCart as RetailIcon,
  HeartPulse as HealthcareIcon,
  DollarSign as FinanceIcon,
  GraduationCap as EducationIcon,
  Truck as LogisticsIcon,
  Cloud,
  Monitor
} from "lucide-react";
import { ManufacturingIcon } from "@/components/icons/ManufacturingIcon";
import { CheckCircle } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

const IndustryIcon = ({ icon: Icon, color }: { icon: React.FC<React.SVGProps<SVGSVGElement>>; color: string }) => (
  <div className={`w-16 h-16 ${color} rounded-full flex items-center justify-center mx-auto mb-4`}>
    <Icon className="w-8 h-8 text-white" />
  </div>
);

export default function ERPCRM() {
  const { openDrawer } = useQuoteDrawer();

  const systems = [
    {
      title: "ERP Systems",
      description: "Comprehensive enterprise resource planning solutions to streamline your business operations.",
      icon: <Database className="h-8 w-8 text-blue-400" />,
      features: ["Financial Management", "Inventory Control", "Supply Chain", "HR Management"],
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "CRM Solutions",
      description: "Customer relationship management systems to enhance customer experiences and sales.",
      icon: <Users className="h-8 w-8 text-purple-400" />,
      features: ["Lead Management", "Sales Pipeline", "Customer Support", "Marketing Automation"],
      color: "from-purple-500 to-purple-600"
    }
  ];

  const benefits = [
    {
      title: "Process Automation",
      description: "Automate repetitive tasks and workflows to increase efficiency and reduce errors.",
      icon: <Workflow className="h-8 w-8 text-green-400" />
    },
    {
      title: "Real-time Analytics",
      description: "Get instant insights into your business performance with comprehensive reporting.",
      icon: <BarChart className="h-8 w-8 text-yellow-400" />
    },
    {
      title: "Scalable Architecture",
      description: "Systems that grow with your business and adapt to changing requirements.",
      icon: <Database className="h-8 w-8 text-blue-400" />
    },
    {
      title: "Integration Capabilities",
      description: "Seamlessly integrate with existing systems and third-party applications.",
      icon: <Users className="h-8 w-8 text-purple-400" />
    }
  ];

  const industries = [
    { name: "Manufacturing", icon: ManufacturingIcon, color: "bg-blue-500" },
    { name: "Retail", icon: RetailIcon, color: "bg-purple-500" },
    { name: "Healthcare", icon: HealthcareIcon, color: "bg-red-500" },
    { name: "Finance", icon: FinanceIcon, color: "bg-green-500" },
    { name: "Education", icon: EducationIcon, color: "bg-yellow-500" },
    { name: "Logistics", icon: LogisticsIcon, color: "bg-indigo-500" }
  ];

  return (
    <>
      <SEO 
        title="ERP & CRM Solutions UAE | Kecly"
        description="Enterprise-grade ERP & CRM systems in Dubai & Sharjah. Streamline operations, automate workflows and boost productivity."
        keywords="erp solutions uae, crm software dubai, business automation sharjah"
      />

      <main className="bg-white text-gray-900">
        <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Business <span className="text-blue-600">Automation</span>
            </h1>
            <p className="text-xl max-w-3xl mx-auto mb-8 text-gray-600">
              Integrated ERP & CRM solutions to streamline your operations
            </p>
          </div>
        </section>

        {/* Service Details */}
        <section className="py-16 container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Why Choose Our ERP/CRM Development?</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-2xl font-semibold mb-4">Custom ERP Development</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                  <span>Streamline financials, inventory, and supply chain</span>
                </li>
                {/* More list items */}
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold mb-4">CRM Solutions</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                  <span>360° customer view and sales pipeline management</span>
                </li>
                {/* More list items */}
              </ul>
            </div>
          </div>

          <WebDesignLocations />

          {/* CTA */}
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Business?</h3>
            <p className="mb-6 max-w-2xl mx-auto text-gray-600">
              Get a free consultation for your custom ERP/CRM solution
            </p>
            <Button 
              size="lg" 
              className="px-8 py-6 text-lg bg-blue-600 hover:bg-blue-700 text-white"
              onClick={openDrawer}
            >
              Get Your Free Quote
            </Button>
          </div>
        </section>

        {/* Systems Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8">
              {systems.map((system, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg"
                >
                  <div className="flex items-center mb-6">
                    {system.icon}
                    <h3 className="text-2xl font-bold ml-4">{system.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{system.description}</p>
                  <ul className="space-y-2">
                    {system.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-12">Key Benefits</h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-6 rounded-lg"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-12">Industries We Serve</h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-md"
                >
                  <IndustryIcon icon={industry.icon} color={industry.color} />
                  <h3 className="text-xl font-semibold">{industry.name}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="ERP & CRM" 
              services={[
                {
                  title: "Cloud Services",
                  description: "Host your ERP/CRM systems in the cloud for scalability and reliability.",
                  icon: <Cloud className="h-8 w-8" />
                },
                {
                  title: "Data Analytics",
                  description: "Extract insights from your business data with advanced analytics.",
                  icon: <BarChart className="h-8 w-8" />
                },
                {
                  title: "Web Design & Development",
                  description: "Build custom interfaces and portals for your ERP/CRM systems.",
                  icon: <Monitor className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>
      </main>
    </>
  );
}
